import { z } from "zod";
import { jobIdSchema } from "./job-schemas";

export const jobApplicationIdSchema = jobIdSchema;
export const jobApplicationStatusSchema = z.enum(["PENDING", "CONFIRMED", "NOT_SELECTED"]);

const versionNoSchema = z.number().int().min(1);
const isoDateTimeSchema = z.string().datetime({ offset: true });

const providerApplicationFields = {
  id: jobApplicationIdSchema,
  jobId: jobIdSchema,
  applicationStatus: jobApplicationStatusSchema,
  versionNo: versionNoSchema,
  appliedAt: isoDateTimeSchema,
} as const;

function addApplicationStateVersionIssue(
  application: { applicationStatus: "PENDING" | "CONFIRMED" | "NOT_SELECTED"; versionNo: number },
  context: z.RefinementCtx,
): void {
  const expectedVersion = application.applicationStatus === "PENDING" ? 1 : 2;
  if (application.versionNo !== expectedVersion) {
    context.addIssue({
      code: "custom",
      path: ["versionNo"],
      message: "Application version must match its state",
    });
  }
}

export const providerApplicationSchema = z
  .strictObject(providerApplicationFields)
  .superRefine(addApplicationStateVersionIssue);

export const customerApplicationSchema = z
  .strictObject({
    ...providerApplicationFields,
    providerId: jobIdSchema,
    providerPublicName: z.string().min(1).max(200),
  })
  .superRefine(addApplicationStateVersionIssue);

const bookingTimezone = "America/Los_Angeles" as const;

function timestampOffset(value: string): string | null {
  const offset = /(Z|[+-]\d{2}:\d{2})$/u.exec(value)?.[1];
  return offset === "Z" ? "+00:00" : (offset ?? null);
}

function timezoneOffsetAt(value: string): string | null {
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return null;
  try {
    const timezoneName = new Intl.DateTimeFormat("en-US", {
      timeZone: bookingTimezone,
      timeZoneName: "longOffset",
    })
      .formatToParts(timestamp)
      .find(({ type }) => type === "timeZoneName")?.value;
    if (timezoneName === undefined) return null;
    if (timezoneName === "GMT") return "+00:00";
    const offset = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/u.exec(timezoneName);
    if (offset === null) return null;
    return `${offset[1]}${offset[2]?.padStart(2, "0")}:${offset[3] ?? "00"}`;
  } catch {
    return null;
  }
}

export const bookingSchema = z
  .strictObject({
    id: jobIdSchema,
    jobId: jobIdSchema,
    applicationId: jobApplicationIdSchema,
    providerId: jobIdSchema,
    providerPublicName: z.string().min(1).max(200),
    jobStatus: z.literal("BOOKED"),
    currency: z.literal("USD"),
    fixedPriceAmountMinor: z.number().int().min(100).max(1_000_000),
    requestStartAt: isoDateTimeSchema,
    requestEndAt: isoDateTimeSchema,
    timezoneRegion: z.literal(bookingTimezone),
    jobVersionNo: versionNoSchema,
    bookedAt: isoDateTimeSchema,
  })
  .superRefine((booking, context) => {
    if (Date.parse(booking.requestEndAt) <= Date.parse(booking.requestStartAt)) {
      context.addIssue({
        code: "custom",
        path: ["requestEndAt"],
        message: "Booking end must follow booking start",
      });
    }
    if (booking.requestStartAt.slice(0, 10) !== booking.requestEndAt.slice(0, 10)) {
      context.addIssue({
        code: "custom",
        path: ["requestEndAt"],
        message: "Booking window must stay on one local date",
      });
    }

    const startOffset = timestampOffset(booking.requestStartAt);
    const endOffset = timestampOffset(booking.requestEndAt);
    if (startOffset !== endOffset) {
      context.addIssue({
        code: "custom",
        path: ["requestEndAt"],
        message: "Booking window must stay on one timezone offset",
      });
    }
    if (startOffset !== timezoneOffsetAt(booking.requestStartAt)) {
      context.addIssue({
        code: "custom",
        path: ["requestStartAt"],
        message: "Booking start offset must match its timezone",
      });
    }
    if (endOffset !== timezoneOffsetAt(booking.requestEndAt)) {
      context.addIssue({
        code: "custom",
        path: ["requestEndAt"],
        message: "Booking end offset must match its timezone",
      });
    }
  });

export const confirmJobApplicationInputSchema = z.strictObject({
  jobVersionNo: versionNoSchema,
  applicationVersionNo: versionNoSchema,
});

export const applyForMarketJobResponseSchema = z
  .strictObject({
    status: z.enum(["applied", "existing"]),
    application: providerApplicationSchema,
  })
  .superRefine((response, context) => {
    if (response.status === "applied" && response.application.applicationStatus !== "PENDING") {
      context.addIssue({
        code: "custom",
        path: ["application", "applicationStatus"],
        message: "A new application must be pending",
      });
    }
  });

export const currentProviderApplicationResponseSchema = z.strictObject({
  status: z.literal("ok"),
  application: providerApplicationSchema.nullable(),
});

export const customerApplicationsResponseSchema = z.strictObject({
  status: z.literal("ok"),
  applications: z.array(customerApplicationSchema).max(50),
});

export const confirmJobApplicationResponseSchema = z.strictObject({
  status: z.enum(["booked", "existing"]),
  booking: bookingSchema,
});

export type JobApplicationStatus = z.infer<typeof jobApplicationStatusSchema>;
export type ProviderApplication = z.infer<typeof providerApplicationSchema>;
export type CustomerApplication = z.infer<typeof customerApplicationSchema>;
export type Booking = z.infer<typeof bookingSchema>;
export type ConfirmJobApplicationInput = z.infer<typeof confirmJobApplicationInputSchema>;
export type ApplyForMarketJobResponse = z.infer<typeof applyForMarketJobResponseSchema>;
export type ConfirmJobApplicationResponse = z.infer<typeof confirmJobApplicationResponseSchema>;
